import { useCallback, useEffect, useRef } from "react";

interface SwipeOptions {
	onSwipeLeft?: () => void;
	onSwipeRight?: () => void;
	threshold?: number;
	enabled?: boolean;
}

export function useSwipe({
	onSwipeLeft,
	onSwipeRight,
	threshold = 60,
	enabled = true,
}: SwipeOptions) {
	const startX = useRef(0);
	const startY = useRef(0);
	const isHorizontal = useRef(false);

	const onTouchStart = useCallback(
		(e: TouchEvent) => {
			if (!enabled) return;
			startX.current = e.touches[0].clientX;
			startY.current = e.touches[0].clientY;
			isHorizontal.current = false;
		},
		[enabled]
	);

	const onTouchMove = useCallback(
		(e: TouchEvent) => {
			if (!enabled) return;
			const dx = e.touches[0].clientX - startX.current;
			const dy = e.touches[0].clientY - startY.current;
			// Determine if this is a horizontal swipe early
			if (Math.abs(dx) > 10 && Math.abs(dx) > Math.abs(dy)) {
				isHorizontal.current = true;
			}
		},
		[enabled]
	);

	const onTouchEnd = useCallback(
		(e: TouchEvent) => {
			if (!enabled || !isHorizontal.current) return;
			const dx = e.changedTouches[0].clientX - startX.current;
			const dy = e.changedTouches[0].clientY - startY.current;
			// Only trigger if predominantly horizontal
			if (Math.abs(dx) > threshold && Math.abs(dx) > Math.abs(dy) * 1.5) {
				if (dx > 0) {
					onSwipeRight?.();
				} else {
					onSwipeLeft?.();
				}
			}
		},
		[enabled, threshold, onSwipeLeft, onSwipeRight]
	);

	useEffect(() => {
		document.addEventListener("touchstart", onTouchStart, { passive: true });
		document.addEventListener("touchmove", onTouchMove, { passive: true });
		document.addEventListener("touchend", onTouchEnd, { passive: true });
		return () => {
			document.removeEventListener("touchstart", onTouchStart);
			document.removeEventListener("touchmove", onTouchMove);
			document.removeEventListener("touchend", onTouchEnd);
		};
	}, [onTouchStart, onTouchMove, onTouchEnd]);
}
